import * as React from "react";
import { useState } from "react";

import { flexRender, ExpandedState } from "@tanstack/react-table";
import { ConfirmDialog } from "primereact/confirmdialog";
import { twMerge } from "tailwind-merge";

import Loader from "@/common/components/Loader";
import ModalBan from "@/modules/profile/components/ModalBan";
import { useGetAllGroups } from "@/modules/profile/hooks/group.hooks";

import ModalUser from "../../../components/ModalUser";
import { HeaderLegend } from "../components/HeaderLegend";
import { DottedTriggerMenu } from "../components/TreeTable/DottedTriggerMenu";
import { useBanManagementModal } from "../hooks/banManagementModal";
import { useEntityRemovalManagementModal } from "../hooks/entityRemovalManagementModal";
import { useGetGroupTreeData } from "../hooks/groupTreeData.hooks";
import { useManagementTableColumns } from "../hooks/managementTableColumns";
import { useGetManagementTableDefinition } from "../hooks/managementTableDefinition";
import { useUserManagementModal } from "../hooks/userManagementModal.hooks";
import { getTopLevelParentIndex } from "../utils/treeTable.utils";

export const ManagementContainer = () => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [menuOpenRowId, setMenuOpenRowId] = useState<string | null>(null);

  const { columns } = useManagementTableColumns();

  const {
    data: allGroups,
    isLoading: isLoadingAllGroups,
    isFetching: isFetchingAllGroups,
  } = useGetAllGroups();

  const { getGroupTreeData } = useGetGroupTreeData();

  // User management modal
  const {
    showUserManagementModal,
    setShowUserManagementModal,
    handleUserManagementModal,
  } = useUserManagementModal();

  // Ban management modal
  const {
    showBanManagementModal,
    setShowBanManagementModal,
    handleBanManagementModal,
  } = useBanManagementModal();

  // Entity removal modal
  const { handleRemoveEntityModal } = useEntityRemovalManagementModal();

  // Transform the groups data into tree data format
  const treeData = React.useMemo(() => {
    if (!allGroups) return [];

    return getGroupTreeData({
      management: allGroups,
      handleUserManagementModal,
      handleBanManagementModal,
      handleRemoveEntityModal,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allGroups]);

  const { table } = useGetManagementTableDefinition({
    treeData,
    columns,
    expanded,
    setExpanded,
  });

  return (
    <div className="relative min-h-[calc(100vh-109px)]">
      <div className="bg-white rounded-2xl px-5 py-5 border border-gray-200">
        <div className="mt-0.5 flex items-center">
          <h2 className="flex flex-1 text-2xl font-bold">Gestion</h2>
          <div className="flex items-center gap-4">
            <HeaderLegend
              icon={<i className="pi pi-building" />}
              text="Groupe"
            />
            <HeaderLegend icon={<i className="pi pi-home" />} text="Agence" />
            <HeaderLegend icon={<i className="pi pi-users" />} text="Equipe" />
            <HeaderLegend
              icon={<i className="pi pi-user" />}
              text="Utilisateur"
            />
          </div>
        </div>
        <div className="mt-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="relative h-[55.5px] bg-purple-50/80 border-l-8 border-l-purple-500 border-b border-b-gray-200">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={twMerge(
                          "text-left py-3 px-4 font-semibold text-gray-600",
                          header.column.id === "name" && "w-[635px]",
                          header.column.id === "role" && "w-[150px]",
                          header.column.id === "sector" && "w-[620px]",
                          header.column.id === "actions" && "w-0 min-w-0"
                        )}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => {
                  const topLevelParentIndex = getTopLevelParentIndex(row);
                  const isEvenTopLevel = topLevelParentIndex % 2 === 0;
                  const borderLeftColor = isEvenTopLevel
                    ? "border-l-pink-200/70"
                    : "border-l-purple-200";

                  return (
                    <tr
                      key={row.id}
                      className={twMerge(
                        "border-l-[5px] [&:not(:last-child)]:border-b [&:not(:last-child)]:border-b-gray-200 hover:bg-blue-100/25",
                        row.getIsExpanded() ||
                          (row.depth > 0 && row.getParentRow()?.getIsExpanded())
                          ? `${borderLeftColor} hover:${borderLeftColor}`
                          : `border-l-gray-100/70 ${menuOpenRowId === row.id ? "hover:border-l-pink-F20089/80" : "hover:border-l-gray-100/70"}`,
                        menuOpenRowId === row.id
                          ? "bg-blue-100/25 border-l-pink-F20089/80"
                          : ""
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="py-3 px-4 align-top"
                          // colSpan={cell.column.id === "actions" ? 2 : undefined}
                          onClick={(e) => {
                            if (cell.column.id === "name") {
                              e.stopPropagation();
                            }
                          }}
                        >
                          {cell.column.id === "actions" &&
                          row.original.actions?.length ? (
                            <div className="flex justify-end w-full">
                              <DottedTriggerMenu
                                entityContext={{
                                  type: row.original.type,
                                  name: row.original.name,
                                }}
                                actions={row.original.actions}
                                onMenuOpenChange={(isOpen) =>
                                  setMenuOpenRowId(isOpen ? row.id : null)
                                }
                              />
                            </div>
                          ) : (
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <ModalUser
        data={showUserManagementModal.user.data}
        show={showUserManagementModal.user.open}
        action={showUserManagementModal.user.action}
        onHide={() =>
          setShowUserManagementModal({
            user: {
              action: "add",
              open: false,
              data: null,
            },
          })
        }
      />
      <ModalBan
        id={showBanManagementModal.id}
        show={showBanManagementModal.open}
        onHide={() =>
          setShowBanManagementModal({
            id: "",
            open: false,
            data: null,
          })
        }
        data={showBanManagementModal.data}
      />
      <ConfirmDialog />
      {(isLoadingAllGroups || isFetchingAllGroups) && (
        <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center bg-white/50 z-50">
          <Loader />
        </div>
      )}
    </div>
  );
};
